import { z } from 'zod';
import { MoneyNum } from './numbers';

/* ───────────────────────── Финансовый план проекта (БМ-13…21, приказ № 29П) ───────────────────────── */

export const FIN_SECTIONS = {
  acts: 'План закрытия АВР',
  revenue: 'План доходов: платежи заказчика',
  purchase: 'План закупок по статьям',
  payroll: 'Расходы на персонал',
  admin: 'Административные расходы',
  tax: 'Налоги и обязательные платежи',
} as const;
export type FinSection = keyof typeof FIN_SECTIONS;
export const FinSectionSchema = z.enum(Object.keys(FIN_SECTIONS) as [FinSection, ...FinSection[]]);

/** Разделы, которые уменьшают деньги проекта */
export const FIN_COST_SECTIONS: FinSection[] = ['purchase', 'payroll', 'admin', 'tax'];

export const FIN_STATUSES = {
  draft: 'Черновик',
  review_pto: 'Объёмы у ПТО',
  review_fm: 'Проверка финансового менеджера',
  review_curator: 'Утверждение куратора',
  approved: 'Утверждён',
  superseded: 'Заменён новой версией',
} as const;
export type FinStatus = keyof typeof FIN_STATUSES;

/** Маршрут ФП (БМ-17, Регламент пп. 3.1–3.8): РП готовит, ПТО подтверждает объёмы, ФМ проверяет, куратор утверждает */
export const FIN_ROUTE = {
  submit: {
    from: ['draft'] as FinStatus[],
    to: 'review_pto' as FinStatus,
    permission: 'budget.edit',
    label: 'Отправить на проверку',
  },
  pto: {
    from: ['review_pto'] as FinStatus[],
    to: 'review_fm' as FinStatus,
    permission: 'psd.approve',
    label: 'Объёмы подтверждены (ПТО)',
  },
  fm: {
    from: ['review_fm'] as FinStatus[],
    to: 'review_curator' as FinStatus,
    permission: 'finance.edit',
    label: 'Проверено финансовым менеджером',
  },
  approve: {
    from: ['review_curator'] as FinStatus[],
    to: 'approved' as FinStatus,
    permission: 'budget.approve',
    label: 'Утвердить (куратор)',
  },
  return: {
    from: ['review_pto', 'review_fm', 'review_curator'] as FinStatus[],
    to: 'draft' as FinStatus,
    /** Фактически проверяется право текущего шага: ПТО, финансового менеджера или куратора */
    permission: 'budget.approve',
    label: 'Вернуть на доработку',
  },
} as const;
export type FinRouteAction = keyof typeof FIN_ROUTE;

/** Право на шаг маршрута; вернуть на доработку может только тот, чей сейчас шаг */
export function finRoutePermission(action: FinRouteAction, status: FinStatus): string {
  if (action !== 'return') return FIN_ROUTE[action].permission;
  if (status === 'review_pto') return FIN_ROUTE.pto.permission;
  if (status === 'review_fm') return FIN_ROUTE.fm.permission;
  return FIN_ROUTE.approve.permission;
}

const Values = z.record(z.string().regex(/^\d{4}-\d{2}$/), MoneyNum);

export const FinRowInput = z.object({
  id: z.uuid().optional(),
  section: FinSectionSchema,
  article: z.string().trim().min(2).max(200).nullable().optional(),
  name: z.string().trim().min(1).max(300),
  values: Values,
});
export type FinRowInput = z.infer<typeof FinRowInput>;

export const FinPlanSettingsInput = z.object({
  contractAmount: MoneyNum.nonnegative().nullable().optional(),
  vatRate: z.number().min(0).max(30).optional(),
  financingRate: z.number().min(0).max(100).optional(),
  startMonth: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
  months: z.number().int().min(1).max(84).optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type FinPlanSettingsInput = z.infer<typeof FinPlanSettingsInput>;

export const FinRouteInput = z.object({
  action: z.enum(['submit', 'pto', 'fm', 'approve', 'return']),
  comment: z.string().trim().max(2000).optional(),
});
export type FinRouteInput = z.infer<typeof FinRouteInput>;

export const FinPlanView = z.object({
  id: z.uuid(),
  number: z.number().int(),
  status: z.enum(Object.keys(FIN_STATUSES) as [FinStatus, ...FinStatus[]]),
  startMonth: z.string(),
  months: z.array(z.string()),
  contractAmount: z.number().nullable(),
  vatRate: z.number(),
  financingRate: z.number(),
  note: z.string().nullable(),
  returnReason: z.string().nullable(),
  route: z.array(
    z.object({ step: z.string(), title: z.string(), by: z.string().nullable(), at: z.string().nullable() }),
  ),
  rows: z.array(
    FinRowInput.extend({
      id: z.uuid(),
      article: z.string().nullable(),
      total: z.number(),
      fact: z.record(z.string(), z.number()),
      factTotal: z.number(),
    }),
  ),
  totals: z.object({
    revenue: z.number(),
    revenueNet: z.number(),
    acts: z.number(),
    costs: z.number(),
    result: z.number(),
    marginPct: z.number().nullable(),
    costOfMoney: z.number(),
    minBalance: z.number(),
    balance: z.record(z.string(), z.number()),
    costOfMoneyByMonth: z.record(z.string(), z.number()),
    revenueFact: z.number(),
    costsFact: z.number(),
  }),
  /** План, законтрактовано, оплачено и остаток по статьям (ЗК-29, ДГ-17) */
  articles: z.array(
    z.object({
      article: z.string(),
      plan: z.number(),
      contracted: z.number(),
      paid: z.number(),
      remainder: z.number(),
    }),
  ),
});
export type FinPlanView = z.infer<typeof FinPlanView>;

export const FinPlansResponse = z.object({
  versions: z.array(
    z.object({ id: z.uuid(), number: z.number().int(), status: z.string(), createdAt: z.string() }),
  ),
  current: FinPlanView.nullable(),
});
export type FinPlansResponse = z.infer<typeof FinPlansResponse>;

/**
 * ФП для ПТО и ГИП без сумм (приложение В.1): строки плана без денег и объёмы из карточки проекта —
 * по ним ПТО подтверждает объёмы на своём шаге маршрута.
 */
export const FinPlanVolumes = z.object({
  versions: z.array(
    z.object({ id: z.uuid(), number: z.number().int(), status: z.string(), createdAt: z.string() }),
  ),
  current: z
    .object({
      id: z.uuid(),
      number: z.number().int(),
      status: z.enum(Object.keys(FIN_STATUSES) as [FinStatus, ...FinStatus[]]),
      months: z.array(z.string()),
      returnReason: z.string().nullable(),
      route: z.array(
        z.object({
          step: z.string(),
          title: z.string(),
          by: z.string().nullable(),
          at: z.string().nullable(),
        }),
      ),
      rows: z.array(
        z.object({
          id: z.uuid(),
          section: FinSectionSchema,
          article: z.string().nullable(),
          name: z.string(),
          /** Месяцы, в которых по строке что-то запланировано — без сумм */
          activeMonths: z.array(z.string()),
        }),
      ),
    })
    .nullable(),
  volumes: z.array(
    z.object({ article: z.string(), name: z.string(), unit: z.string(), quantity: z.number() }),
  ),
});
export type FinPlanVolumes = z.infer<typeof FinPlanVolumes>;

/** Вознаграждение по банковской гарантии: сумма × ставка / 365 × дни (БМ-20, лист «Расходы по БГ») */
export function bankGuaranteeFee(amount: number, ratePct: number, days: number): number {
  return Math.round(((amount * ratePct) / 100 / 365) * days * 100) / 100;
}

export function monthsFrom(start: string, count: number): string[] {
  const [y, m] = start.split('-').map(Number) as [number, number];
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 1 + i, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}

/** Название статьи расходов: одна из двенадцати или утверждённая по запросу (БМ-14, БМ-15) */
export const ArticleName = z.string().trim().min(2).max(200);

export const COST_ARTICLE_STATUSES = {
  requested: 'Ждёт проверки финансового менеджера',
  checked: 'Ждёт решения Директора',
  approved: 'Утверждена',
  rejected: 'Отклонена',
} as const;
export type CostArticleStatus = keyof typeof COST_ARTICLE_STATUSES;

export const CostArticleView = z.object({
  id: z.uuid(),
  name: z.string(),
  reason: z.string(),
  status: z.enum(Object.keys(COST_ARTICLE_STATUSES) as [CostArticleStatus, ...CostArticleStatus[]]),
  requestedBy: z.string().nullable(),
  requestedAt: z.string(),
  checkedBy: z.string().nullable(),
  checkedAt: z.string().nullable(),
  decidedBy: z.string().nullable(),
  decidedAt: z.string().nullable(),
  rejectReason: z.string().nullable(),
});
export type CostArticleView = z.infer<typeof CostArticleView>;

export const CostArticlesResponse = z.object({
  /** Все статьи, доступные в формах: двенадцать утверждённых и утверждённые по запросу */
  active: z.array(z.string()),
  requests: z.array(CostArticleView),
  can: z.object({ request: z.boolean(), check: z.boolean(), decide: z.boolean() }),
});
export type CostArticlesResponse = z.infer<typeof CostArticlesResponse>;

export const CostArticleRequestInput = z.object({
  name: ArticleName,
  reason: z.string().trim().min(5).max(1000),
});

export const CostArticleDecisionInput = z.object({
  approve: z.boolean(),
  comment: z.string().trim().max(1000).optional(),
});

/**
 * ФП рядом с первоначальным расчётом себестоимости (БМ-18, п. 3.4 Регламента): сметная и плановая
 * себестоимость бюджета проекта против плановых закупок и расходов ФП, плановый финансовый результат
 * против плановой маржи бюджета.
 */
export const FinPlanBaseline = z.object({
  planId: z.uuid(),
  planNumber: z.number().int(),
  /** Версия бюджета, с которой сравнивается ФП; null — бюджета у проекта нет */
  budgetVersionId: z.uuid().nullable(),
  budget: z.object({
    estimateAmount: z.number(),
    budgetCost: z.number(),
    plannedCost: z.number(),
    plannedMargin: z.number(),
    plannedMarginPct: z.number().nullable(),
  }),
  plan: z.object({
    revenueNet: z.number(),
    purchase: z.number(),
    payroll: z.number(),
    admin: z.number(),
    tax: z.number(),
    costs: z.number(),
    costOfMoney: z.number(),
    result: z.number(),
    marginPct: z.number().nullable(),
  }),
  diff: z.object({
    /** Расходы ФП (без налогов и платы за деньги) − плановая себестоимость бюджета */
    cost: z.number(),
    /** Финансовый результат ФП − плановая маржа бюджета */
    result: z.number(),
  }),
  /**
   * Сравнение по статьям ФП (БМ-18): бюджет — расклад `byCostType` и затраты площадки;
   * план — строки закупок, персонала и административных. Налоги не входят.
   * `article: null` — без статьи или не разнесено по статьям (раздел без выбранного варианта).
   */
  byArticle: z.array(
    z.object({
      article: z.string().nullable(),
      budget: z.number(),
      plan: z.number(),
      diff: z.number(),
    }),
  ),
});
export type FinPlanBaseline = z.infer<typeof FinPlanBaseline>;
